import React, { useState, useEffect } from 'react';

const DEFAULT_SETTINGS = {
  app_name: 'ConstructionPro',
  short_name: 'ConstrPro',
  theme_color: '#1e3a5f',
  background_color: '#ffffff',
  offline_enabled: true,
  install_prompt_enabled: true
};

const PWASettingsTab = ({ settings, onSave, saving }) => {
  const [form, setForm] = useState({ ...DEFAULT_SETTINGS, ...settings });

  useEffect(() => {
    setForm({ ...DEFAULT_SETTINGS, ...settings });
  }, [settings]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* App Identity */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-base font-semibold text-foreground mb-4">App Identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">App Name</label>
            <input
              type="text"
              value={form?.app_name || ''}
              onChange={(e) => handleChange('app_name', e?.target?.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="ConstructionPro"
            />
            <p className="text-xs text-muted-foreground mt-1">Full name shown in app stores and splash screens</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Short Name</label>
            <input
              type="text"
              value={form?.short_name || ''}
              onChange={(e) => handleChange('short_name', e?.target?.value)}
              maxLength={12}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="ConstrPro"
            />
            <p className="text-xs text-muted-foreground mt-1">Shown under home screen icon (max 12 chars)</p>
          </div>
        </div>
      </div>
      {/* Theme Colors */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-base font-semibold text-foreground mb-4">Theme Colors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Theme Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form?.theme_color || '#1e3a5f'}
                onChange={(e) => handleChange('theme_color', e?.target?.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer"
              />
              <input
                type="text"
                value={form?.theme_color || ''}
                onChange={(e) => handleChange('theme_color', e?.target?.value)}
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="#1e3a5f"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Browser UI and status bar color</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Background Color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form?.background_color || '#ffffff'}
                onChange={(e) => handleChange('background_color', e?.target?.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer"
              />
              <input
                type="text"
                value={form?.background_color || ''}
                onChange={(e) => handleChange('background_color', e?.target?.value)}
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="#ffffff"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Splash screen background color</p>
          </div>
        </div>
      </div>
      {/* Feature Toggles */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-base font-semibold text-foreground mb-4">Features</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Offline Mode</p>
              <p className="text-xs text-muted-foreground">Cache app shell for offline access via service worker</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('offline_enabled', !form?.offline_enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form?.offline_enabled ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form?.offline_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Install Prompt</p>
              <p className="text-xs text-muted-foreground">Show native install banner on supported browsers</p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('install_prompt_enabled', !form?.install_prompt_enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form?.install_prompt_enabled ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form?.install_prompt_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          Save PWA Settings
        </button>
      </div>
    </form>
  );
};

export default PWASettingsTab;
